import type { SourceReportKind } from '@buakei/types';
import { Button } from '@buakei/ui';
import { useMutation } from '@tanstack/react-query';
import { Flag } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useAuth } from '@/auth/session';
import { reportApi } from '@/services/api';

/** What a reader can report about a document (ADR-026), in their words. */
export const REPORT_KIND_LABEL: Record<SourceReportKind, string> = {
  text_error: 'The text shown does not match the original',
  wrong_document: 'Wrong document, title, year or court',
  citation_problem: 'An answer cites it for something it does not say',
  outdated_law: 'It has been amended or repealed',
  other: 'Something else',
};

/**
 * "Report a problem" with one document (ADR-026). Reports go to the editorial team; they
 * change nothing by themselves. Shown only to signed-in, verified readers.
 */
export function ReportProblem({
  versionId,
  passageId,
  context,
  label = 'Report a problem',
}: {
  versionId: string;
  passageId?: string;
  context: 'library' | 'answer' | 'memo';
  label?: string;
}) {
  const { session, accounts } = useAuth();
  const tokens = session?.provider === 'firebase' && session.emailVerified ? accounts : null;
  const kindId = useId();
  const textId = useId();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<SourceReportKind>('text_error');
  const [description, setDescription] = useState('');
  const send = useMutation({
    mutationFn: () =>
      reportApi.create(tokens!, {
        versionId,
        ...(passageId ? { passageId } : {}),
        kind,
        description,
        context,
      }),
  });
  if (!tokens) return null;
  if (send.isSuccess) {
    return (
      <p role="status" className="text-sm text-ink-900">
        Thank you. The editorial team will check it against the original.
      </p>
    );
  }
  if (!open) {
    return (
      <Button variant="link" size="sm" className="self-start" onClick={() => setOpen(true)}>
        <Flag aria-hidden /> {label}
      </Button>
    );
  }
  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-line-200 bg-white p-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        send.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={kindId} className="text-sm font-medium text-ink-900">
          What is wrong?
        </label>
        <select
          id={kindId}
          value={kind}
          onChange={(e) => setKind(e.target.value as SourceReportKind)}
          className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
        >
          {(Object.keys(REPORT_KIND_LABEL) as SourceReportKind[]).map((k) => (
            <option key={k} value={k}>
              {REPORT_KIND_LABEL[k]}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={textId} className="text-sm font-medium text-ink-900">
          Details
        </label>
        <textarea
          id={textId}
          required
          minLength={10}
          maxLength={2000}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Where it is (section or page) and what it should say"
          className="rounded-md border border-line-200 bg-white p-2 text-ink-900 placeholder:text-muted-600"
        />
        <p className="text-sm text-muted-600">
          Do not include client names or confidential facts: the editorial team reads reports.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={send.isPending || description.trim().length < 10}>
          {send.isPending ? 'Sending…' : 'Send report'}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {send.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {send.error.message}
        </p>
      ) : null}
    </form>
  );
}
